import { AnimatePresence, motion } from "framer-motion";
import { Camera, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router";
import { useState, useMemo, useEffect } from "react";
import { Button } from "@photo-booth/ui/button";
import { usePaymentStore } from "@/lib/store/paymentStore";
import { cn } from "@photo-booth/ui";

// Routes where the banner should NOT appear
const EXCLUDED_ROUTES = ["/camera", "/result", "/payment", "/error"] as const;

interface BackToCameraReminderBannerProps {
  className?: string;
}

export const BackToCameraReminderBanner: React.FC<
  BackToCameraReminderBannerProps
> = ({ className }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { paymentPayload } = usePaymentStore();
  const [isDismissed, setIsDismissed] = useState(false);

  // Reset dismissed state when payment payload changes (new payment)
  useEffect(() => {
    if (paymentPayload?.token) {
      setIsDismissed(false);
    }
  }, [paymentPayload?.token]);

  const shouldShow = useMemo(() => {
    if (!paymentPayload?.token) return false;

    if (EXCLUDED_ROUTES.some((route) => location.pathname.startsWith(route))) {
      return false;
    }

    if (isDismissed) return false;

    return true;
  }, [paymentPayload, location.pathname, isDismissed]);

  const handleNavigateToCamera = () => {
    navigate("/camera");
  };

  const handleDismiss = () => {
    setIsDismissed(true);
  };

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
          }}
          className={cn(
            "fixed bottom-0 left-0 right-0 z-40",
            "pb-[env(safe-area-inset-bottom)]",
            className
          )}
        >
          <div className="mx-4 mb-4 p-4 rounded-xl bg-vintage-red/95 backdrop-blur-sm shadow-lg">
            <div className="flex items-center justify-between gap-3">
              {/* Content */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Camera className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium text-sm">
                    Tap to continue taking photos
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  onClick={handleNavigateToCamera}
                  size="sm"
                  className="bg-white text-vintage-red hover:bg-white/90 font-medium"
                >
                  Continue
                </Button>
                <button
                  onClick={handleDismiss}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors"
                  aria-label="Dismiss reminder"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
